import { useState } from 'react';
import { agencyInfo } from '../../../mocks/dashboard';

type StaffRole = 'Owner' | 'Admin' | 'Senior Agent' | 'Agent' | 'Finance' | 'Viewer';
type StaffStatus = 'Active' | 'Inactive' | 'Invited';

interface StaffMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: StaffRole;
  status: StaffStatus;
  avatar: string;
  lastActive: string;
  bookings: number;
}

const ROLE_STYLES: Record<StaffRole, string> = {
  Owner:        'bg-amber-100 text-amber-700',
  Admin:        'bg-rose-100 text-rose-700',
  'Senior Agent': 'bg-teal-100 text-teal-700',
  Agent:        'bg-emerald-100 text-emerald-700',
  Finance:      'bg-sky-100 text-sky-700',
  Viewer:       'bg-gray-100 text-gray-500',
};

const STATUS_STYLES: Record<StaffStatus, string> = {
  Active:   'bg-emerald-100 text-emerald-700',
  Inactive: 'bg-gray-100 text-gray-500',
  Invited:  'bg-amber-100 text-amber-600',
};

const initStaff: StaffMember[] = [
  { id: 's1', name: 'Mohammed Khalid',   email: 'mk@skywavetravel.ae',       phone: '+971 50 100 2000', role: 'Owner',        status: 'Active',   avatar: 'MK', lastActive: '2 min ago',   bookings: 148 },
  { id: 's2', name: 'Sara Ahmed',         email: 'sara@skywavetravel.ae',     phone: '+971 50 200 3001', role: 'Senior Agent', status: 'Active',   avatar: 'SA', lastActive: '15 min ago',  bookings: 94  },
  { id: 's3', name: 'Fatima Al-Zahra',   email: 'fatima@skywavetravel.ae',   phone: '+971 55 300 4002', role: 'Agent',        status: 'Active',   avatar: 'FA', lastActive: '1 hour ago',  bookings: 61  },
  { id: 's4', name: 'Omar Al-Rashidi',   email: 'omar@skywavetravel.ae',     phone: '+971 52 400 5003', role: 'Agent',        status: 'Active',   avatar: 'OA', lastActive: '3 hours ago', bookings: 45  },
  { id: 's5', name: 'Layla Hassan',      email: 'layla@skywavetravel.ae',    phone: '+971 56 500 6004', role: 'Finance',      status: 'Active',   avatar: 'LH', lastActive: 'Today 9 AM',  bookings: 0   },
  { id: 's6', name: 'Khalid Mansouri',   email: 'khalid.m@skywavetravel.ae', phone: '+971 54 600 7005', role: 'Agent',        status: 'Inactive', avatar: 'KM', lastActive: '2 weeks ago', bookings: 22  },
  { id: 's7', name: 'Reem Al-Nuaimi',    email: 'reem@skywavetravel.ae',     phone: '', role: 'Viewer',       status: 'Invited',  avatar: 'RN', lastActive: 'Never',       bookings: 0   },
];

export default function StaffSection() {
  const brand = agencyInfo.brandColor;
  const [staff, setStaff] = useState<StaffMember[]>(initStaff);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Form state
  const [fName, setFName] = useState('');
  const [fEmail, setFEmail] = useState('');
  const [fPhone, setFPhone] = useState('');
  const [fRole, setFRole] = useState<StaffRole>('Agent');

  const roles: StaffRole[] = ['Owner', 'Admin', 'Senior Agent', 'Agent', 'Finance', 'Viewer'];

  const openAdd = () => {
    setEditId(null);
    setFName(''); setFEmail(''); setFPhone(''); setFRole('Agent');
    setShowForm(true);
  };

  const openEdit = (s: StaffMember) => {
    setEditId(s.id);
    setFName(s.name); setFEmail(s.email); setFPhone(s.phone); setFRole(s.role);
    setShowForm(true);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (editId) {
      setStaff((prev) => prev.map((s) => s.id === editId ? { ...s, name: fName, email: fEmail, phone: fPhone, role: fRole } : s));
    } else {
      const newMember: StaffMember = {
        id: `s${Date.now()}`, name: fName, email: fEmail, phone: fPhone,
        role: fRole, status: 'Invited', avatar: fName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase(),
        lastActive: 'Never', bookings: 0,
      };
      setStaff((prev) => [...prev, newMember]);
    }
    setSaved(true);
    setTimeout(() => { setSaved(false); setShowForm(false); }, 1500);
  };

  const toggleStatus = (id: string) => {
    setStaff((prev) => prev.map((s) => s.id === id && s.role !== 'Owner'
      ? { ...s, status: s.status === 'Active' ? 'Inactive' : 'Active' }
      : s
    ));
  };

  const activeCount = staff.filter((s) => s.status === 'Active').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-900 text-base">Team Members</h3>
          <p className="text-sm text-gray-400 mt-0.5">{activeCount} active · {staff.length} total</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: brand }}
        >
          <i className="ri-user-add-line" />
          Invite Staff
        </button>
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div className="border border-gray-200 rounded-2xl p-5 bg-gray-50/50">
          <p className="font-semibold text-gray-800 text-sm mb-4">{editId ? 'Edit Staff Member' : 'Invite New Staff Member'}</p>
          {saved ? (
            <div className="flex items-center gap-2 text-emerald-600 text-sm justify-center py-4">
              <i className="ri-checkbox-circle-line text-xl" />
              {editId ? 'Changes saved!' : 'Invitation sent!'}
            </div>
          ) : (
            <form onSubmit={handleSave} className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
                <input type="text" required value={fName} onChange={(e) => setFName(e.target.value)}
                  placeholder="e.g. Sara Ahmed"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email Address</label>
                <input type="email" required value={fEmail} onChange={(e) => setFEmail(e.target.value)}
                  placeholder="staff@agency.com"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone (optional)</label>
                <input type="text" value={fPhone} onChange={(e) => setFPhone(e.target.value)}
                  placeholder="+971 50 000 0000"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                <select value={fRole} onChange={(e) => setFRole(e.target.value as StaffRole)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white cursor-pointer">
                  {roles.filter((r) => r !== 'Owner').map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="col-span-2 flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 py-2 rounded-xl text-white text-sm font-semibold cursor-pointer"
                  style={{ backgroundColor: brand }}>
                  {editId ? 'Save Changes' : 'Send Invitation'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Staff Table */}
      <div className="border border-gray-100 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs text-gray-400 uppercase tracking-wider">
              <th className="text-left px-5 py-3 font-medium">Staff Member</th>
              <th className="text-left px-4 py-3 font-medium">Role</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-right px-4 py-3 font-medium">Bookings</th>
              <th className="text-left px-4 py-3 font-medium">Last Active</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {staff.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: brand }}>
                      {s.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{s.name}</p>
                      <p className="text-xs text-gray-400">{s.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3.5">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${ROLE_STYLES[s.role]}`}>{s.role}</span>
                </td>
                <td className="px-4 py-3.5">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[s.status]}`}>{s.status}</span>
                </td>
                <td className="px-4 py-3.5 text-right">
                  <span className="font-semibold text-gray-700">{s.bookings > 0 ? s.bookings : '—'}</span>
                </td>
                <td className="px-4 py-3.5 text-gray-400 text-xs">{s.lastActive}</td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(s)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                      <i className="ri-edit-line text-sm" />
                    </button>
                    {s.role !== 'Owner' && (
                      <button onClick={() => toggleStatus(s.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                        <i className={`text-sm ${s.status === 'Active' ? 'ri-forbid-line' : 'ri-checkbox-circle-line'}`} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Role Legend */}
      <div className="border border-gray-100 rounded-2xl p-5">
        <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Role Permissions</p>
        <div className="grid grid-cols-2 gap-3 text-xs">
          {[
            { role: 'Owner', desc: 'Full access. Manage billing, all settings, all staff.' },
            { role: 'Admin', desc: 'Full access except billing. Can manage staff.' },
            { role: 'Senior Agent', desc: 'CRM, Bookings, Itinerary, Products, Invoices.' },
            { role: 'Agent', desc: 'CRM, Bookings, Itinerary. Cannot edit invoices.' },
            { role: 'Finance', desc: 'Invoices, Vouchers, Payments only.' },
            { role: 'Viewer', desc: 'Read-only access to all modules.' },
          ].map(({ role, desc }) => (
            <div key={role} className="flex items-start gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${ROLE_STYLES[role as StaffRole]}`}>{role}</span>
              <p className="text-gray-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
